<template>
  <div>
    <div v-if="loaded && (actualSites.length > 1 || currentExportLayout.type === ExportType.Simplified)">
      <InterlayerPage :filters="filters" class="page" :title="$t('sections.mainSynthesis')" :transports="mainTransportModes" />
      <div v-for="(transport, transportIdx) in mainSynthesisData" :key="transportIdx">
        <component
          :is="section.name"
          v-for="(section, sectionIdx) in transport.sections"
          :key="section.name"
          :page-count="tableOfContent[0].transports[transportIdx].sections[sectionIdx].page || 0"
          class="analysis-zone"
          :site="{ name: $t('sections.mainSynthesis') }"
          :filters="filters"
          :comment="comment(transportIdx, sectionIdx)"
          :data="transport"
          :study="study"
          :name="name"
        />
      </div>
    </div>

    <template v-if="currentExportLayout.type !== ExportType.Simplified">
      <div v-for="(site, index) in computedData" :key="site.id">
        <InterlayerPage
          :disable-count="tableOfContent[actualSites.length > 1 ? index + 1 : index].page || 0"
          :filters="filters"
          :title="site.name"
          :site="site.name"
          :baseline="site.address"
          :transports="getTransportModes(site)"
        />
        <IntroductionProvenancePage
          :disable-count="tableOfContent[actualSites.length > 1 ? index + 1 : index].page + 1 || 0"
          :study="study"
          :filters="filters"
          :filterCollaborators="filterCollaborators(exportFilters, site)"
        />
        <div v-for="(transport, idx) in site" :key="idx">
          <component
            :is="section.name"
            v-for="(section, idx2) in transport.sections"
            :key="section.name"
            :page-count="
              tableOfContent[index + indexOffset].transports[idx].sections[idx2] ? tableOfContent[index + indexOffset].transports[idx].sections[idx2].page : 0
            "
            class="analysis-zone"
            :filters="filters"
            :comment="actualSites[index].transports[idx].sections[idx2].comment"
            :data="transport"
            :name="name"
            :site="site"
            :study="study"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ComputedData from '@/pure/ComputedData';
import TransportMode from '@/pure/TransportMode';

import FilterSpecs from '@/components/Export/filterSpecs';

import InterlayerPage from '@/components/Report/InterlayerPage';
import MainSynthesis from '@/components/Export/Analysis/MainSynthesis';
import Synthesis from '@/components/Export/Analysis/Synthesis';
import Transit from '@/components/Export/Analysis/Transit';
import Impact from '@/components/Export/Analysis/Impact';
import Outlier from '@/components/Export/Analysis/Outlier';
import Environmental from '@/components/Export/Analysis/Environmental';
import IntroductionProvenancePage from '@/components/Report/IntroductionProvenancePage';
import { eventBus } from '@/eventBus';
import ExportType from '@/pure/ExportType';

export default {
  components: {
    InterlayerPage,
    MainSynthesis,
    Synthesis,
    Transit,
    Impact,
    Outlier,
    Environmental,
    IntroductionProvenancePage,
  },

  props: ['index', 'filters', 'name'],

  data() {
    return {
      mainSynthesisData: [],
      mainTransportModes: [],
      computedData: [],
      activeSection: null,
    };
  },

  computed: {
    ...mapGetters(['exportActualSites', 'currentExportLayout', 'totalPages', 'tableOfContent', 'globalFilters', 'ruleFilters', 'study']),

    ExportType() {
      return ExportType;
    },

    indexOffset() {
      return this.exportActualSites && this.exportActualSites.length > 1 ? 1 : 0;
    },

    currentPages() {
      return this.tableOfContent.map(item => item.page);
    },

    sites() {
      return this.currentExportLayout.sites;
    },

    mainSynthesisSite() {
      const mainSynthesis = this.currentExportLayout.sites.filter(site => site.id === 0);
      return mainSynthesis.length > 0 ? mainSynthesis[0] : null;
    },

    actualSites() {
      return this.currentExportLayout.sites.filter(site => site.type === 1);
    },

    targetSites() {
      return this.currentExportLayout.sites.filter(site => site.type === 2);
    },

    loaded() {
      return this.currentExportLayout && this.study;
    },

    exportFilters() {
      const { filters } = this.currentExportLayout;
      return Object.keys(filters).reduce((acc, key) => {
        const properObject = FilterSpecs[key];
        const filter = filters[key];
        if (properObject) {
          const ruleFilterLength = this.ruleFilters[properObject.key] ? this.ruleFilters[properObject.key].length : 0;
          const formatFilter = [];
          filter.forEach(item => {
            if (item.enabled) {
              formatFilter.push(item.value);
            }
          });
          if (formatFilter.length < ruleFilterLength) {
            acc[properObject.key] = formatFilter;
          }
        }
        return acc;
      }, {});
    },
  },

  created() {
    this.computeData = new ComputedData();
    this.actualSites.forEach((site, idx) => {
      const computedSite = [];
      if (idx === 0) {
        site.transports.forEach(transport => {
          const transportMode = this.getTransportMode(transport);
          this.mainSynthesisData.push(this.getData(null, transportMode, transport.sections));
        });
        this.mainTransportModes = this.getTransportModes(this.mainSynthesisData);
      }

      const currentSite = this.study.sites.find(item => {
        return item.id === site.id;
      });

      site.transports.forEach(transport => {
        const transportMode = this.getTransportMode(transport);
        computedSite.push(this.getData(site, transportMode, transport.sections));
      });

      if (currentSite) {
        computedSite.name = currentSite.name;
        computedSite.address = currentSite.address;
        computedSite.id = currentSite.id;
      }
      this.computedData.push(computedSite);
    });
  },

  mounted() {
    eventBus.on('selectSection', this.updateActiveSection);
  },

  beforeUnmount() {
    eventBus.off('selectSection', this.updateActiveSection);
    this.$store.dispatch('clearExportLayout');
  },

  methods: {
    getPage(index) {
      return this.tableOfContent[index] ? this.tableOfContent[index].page : 0;
    },

    getData(site, transportMode, sections) {
      this.globalFilters.currentMatchingTime = transportMode.key;
      let filters = JSON.parse(JSON.stringify(this.$store.getters.filters));

      if (site) {
        filters.actualSite.push(site.name);
      } else {
        filters.actualSite = this.actualSites.map(item => item.name);
      }

      Object.keys(this.exportFilters).forEach(item => {
        filters[item] = this.exportFilters[item];
      });

      filters['transportType'] = filters['transportType'].map(e => {
        switch (e.toLowerCase()) {
          case 'bike':
            return 'bicycle';
          case 'car':
            if (this.currentExportLayout.individual_transport_type === 2) {
              return 'private_without_toll';
            }
            return 'private';
          case 'transport':
            if (this.currentExportLayout.public_transport_type === 2) {
              return 'public2025';
            } else if (this.currentExportLayout.public_transport_type === 3) {
              return 'public2030';
            } else if (this.currentExportLayout.public_transport_type === 4) {
              return 'public2027';
            } else if (this.currentExportLayout.public_transport_type === 5) {
              return 'public2031';
            }
            return 'public';
          case 'none':
            return 'none';
          default:
            return e;
        }
      });

      const collaborators = this.filterCollaborators(filters);
      const computedData = this.computeData.reduceData(collaborators, this.study, this.globalFilters);

      computedData.currentTransportFilter = computedData.currentTransportFilter.includes('private') ? 'private' : computedData.currentTransportFilter;

      switch (computedData.currentTransportFilter) {
        case 'private':
          if (this.currentExportLayout.individual_transport_type === 2) {
            computedData.currentTransportFilter = 'private_without_toll';
          } else {
            computedData.currentTransportFilter = 'private';
          }
          break;
        case 'none':
          computedData.currentTransportFilter = 'none';
          break;
        default:
          break;
      }

      const targetSiteIds = this.targetSites.map(targetSite => targetSite.id);
      computedData.sites = computedData.sites.filter(s => targetSiteIds.includes(s.id));
      computedData.transportMode =
        TransportMode.find(transportMode => transportMode.key === computedData.currentTransportFilter) || JSON.parse(JSON.stringify(transportMode));

      if (sections) {
        computedData.sections = sections;
      }
      return computedData;
    },

    getTransportMode(transport) {
      return TransportMode.find(mode => mode.index === transport.type);
    },

    getTransportModes(current) {
      return current.map(item => {
        return item.transportMode;
      });
    },

    filterCollaborators(rawFilters, site = null) {
      let collaborators = this.study.collaborators.filter(item => item.address != null && item.sites && item.sites.length > 0);

      for (const filter in rawFilters) {
        if (rawFilters[filter].length > 0) {
          collaborators = collaborators.filter(person => {
            const filters = rawFilters[filter].map(e => (e === '' ? 'Non renseigné'.toLowerCase() : e.toLowerCase()));
            return filters.includes(person.details[filter].toLowerCase());
          });
        }
      }
      if (site) {
        collaborators = collaborators.filter(collab => {
          return collab.sites.some(s => s.id === site.id);
        });
      }

      if (!this.study.withWalkingTimes || this.study.maximum_travel_time_enabled === false) return collaborators;

      const currentTransportFilter = this.globalFilters.currentMatchingTime;
      const globalTargetSiteFilter = this.globalFilters.targetSites;
      const generalCurrentTransportFilter = currentTransportFilter
        .replaceAll(/\d/g, '') // replace 'public2025' or 'public2030' or 'public2027' or 'public2031' to 'public'
        .replaceAll('_without_toll', ''); // replace 'private_without_toll' to 'private'
      let maximumTravelTimeForCurrentTransport = this.study[generalCurrentTransportFilter + '_maximum_travel_time'];

      return collaborators.filter(collaborator => {
        let collaboratorTransport;
        if (currentTransportFilter === 'custom') {
          collaboratorTransport = collaborator.details.transportType;
          maximumTravelTimeForCurrentTransport = this.study[collaboratorTransport + '_maximum_travel_time'];
        }

        const allTargetSitesTooFar = globalTargetSiteFilter.every(targetSiteId => {
          const collaboratorTargetSite = collaborator.sites.find(site => site.id === targetSiteId);
          if (!collaboratorTargetSite) return true;

          if (currentTransportFilter === 'optimized') {
            collaboratorTransport = collaboratorTargetSite.optimizedSite;
            maximumTravelTimeForCurrentTransport = this.study[collaboratorTransport + '_maximum_travel_time'];
          }

          const collaboratorTimeForCurrentTransport = collaboratorTargetSite.times[0][collaboratorTransport || currentTransportFilter] || 0;

          return maximumTravelTimeForCurrentTransport < collaboratorTimeForCurrentTransport;
        });

        return !allTargetSitesTooFar;
      });
    },

    updateActiveSection(data) {
      this.activeSection = data;
    },

    isActive(site, transport, section) {
      return this.activeSection.siteIdx === site && this.activeSection.transportIdx === transport && this.activeSection.sectionIdx === section;
    },

    comment(transportIdx, sectionIdx) {
      return this.mainSynthesisSite ? this.mainSynthesisSite.transports[transportIdx].sections[sectionIdx].comment : '';
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/A4.scss';
.page {
  width: 297mm;
  height: 209mm;
  background-color: white;
  box-sizing: border-box;
  page-break-after: always;
  display: flex;
  flex-direction: column;

  // transform: scale(0.5);
  // margin: -320px auto;

  margin: 64px auto;
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0 4px 24px 0 rgba(0, 0, 0, 0.1),
    0 4px 8px -4px rgba(0, 0, 0, 0.4);
}

@media print {
  .page {
    transform: scale(1);
    margin: 0;
    border-radius: 0;
    box-shadow: none;
  }
}
</style>
